import { call, put, takeEvery, select } from "redux-saga/effects"
import { 
  DECODE_REQUEST, 
  TOGGLE_BIT,
  INITIALIZE_WITH_DEFAULT,
  TLV_PARSE_REQUEST
} from "./actionTypes"
import {
  decodeSuccess,
  decodeFailure,
  updateHexValue,
  updateLocalMatrix,
  tlvParseSuccess,
  tlvParseFailure
} from "./actions"
import { getResponse } from "../../helpers/api_helper"
import { DecoderHelpers } from "../../components/Decoders/DecoderHelpers"

function* handleDecodeRequest(action) {
  try {
    const { data, endpoint, decoderType } = action.payload
    
    const response = yield call(getResponse, { data }, endpoint)
    
    if (response.resultCode !== "-1" && response.resultCode !== undefined) {
      yield put(decodeSuccess(response, decoderType))
      
      const history = JSON.parse(localStorage.getItem("jsonHistory")) || []
      history.push(response)
      localStorage.setItem("jsonHistory", JSON.stringify(history))
    } else {
      yield put(decodeFailure(response.resultDesc || "Decode operation failed", decoderType))
      
      const history = JSON.parse(localStorage.getItem("jsonHistory")) || []
      history.push(response)
      localStorage.setItem("jsonHistory", JSON.stringify(history))
    }
  } catch (error) {
    console.error('Decode API Error:', error)
    yield put(decodeFailure(error.message || "Network error occurred", action.payload.decoderType))
  }
}

function* handleBitToggle(action) {
  try {
    const { byteIndex, bitPosition, currentValue, hexValue, decoderType } = action.payload
    
    const state = yield select()
    const decoderState = state.decoder
    
    if (!decoderState.localMatrixData) return
    
    const templateConfig = {
      pageKey: decoderType,
      fieldConfig: {
        validation: { min: getMinLengthByType(decoderType) }
      }
    }
    
    const newHexValue = DecoderHelpers.handleBitToggle(
      byteIndex, 
      bitPosition, 
      currentValue, 
      hexValue, 
      templateConfig.fieldConfig
    )
    
    yield put(updateHexValue(newHexValue))
    
    const updatedMatrix = updateLocalMatrixData(
      decoderState.localMatrixData,
      byteIndex,
      bitPosition,
      !currentValue,
      newHexValue,
      decoderState.builderData,
      templateConfig
    )
    
    if (updatedMatrix) {
      yield put(updateLocalMatrix(updatedMatrix))
    }
    
    DecoderHelpers.updateFormField(newHexValue)
    
  } catch (error) {
    console.error('Bit toggle error:', error)
  }
}

const getMinLengthByType = (decoderType) => {
  const lengths = {
    tvr: 10,
    cvmResults: 6,
    aip: 4,
    ctq: 4,
    appUsageControl: 4,
    termCap: 6,
    ttq: 8,
    tag9F40: 10,
    cvmList: 20
  }
  return lengths[decoderType]
}

function updateLocalMatrixData(localMatrixData, byteIndex, bitPosition, newValue, newHexValue, builderData, templateConfig) {
  if (!localMatrixData) return null
  
  const updatedMatrix = { ...localMatrixData }
  const byteKey = `byte${byteIndex}`
  
  if (updatedMatrix[byteKey]) {
    updatedMatrix[byteKey] = {
      ...updatedMatrix[byteKey],
      rows: updatedMatrix[byteKey].rows.map(row => {
        if (row.bit === bitPosition) {
          const updatedMeaning = DecoderHelpers.updateBitMeaning(
            byteIndex,
            bitPosition,
            newValue,
            localMatrixData,
            builderData,
            templateConfig,
            newHexValue
          )
          
          return {
            ...row,
            value: newValue,
            meaning: updatedMeaning || row.meaning
          }
        }
        
        if (templateConfig.pageKey === 'cvmResults' && byteIndex === 1 && 
            bitPosition >= 1 && bitPosition <= 6 && 
            row.bit >= 1 && row.bit <= 6) {
          
          const updatedMeaning = DecoderHelpers.updateBitMeaning(
            byteIndex,
            row.bit,
            row.value,
            localMatrixData,
            builderData,
            templateConfig,
            newHexValue
          )
          
          return {
            ...row,
            meaning: updatedMeaning || row.meaning
          }
        }
        
        if (templateConfig.pageKey === 'tvr' && byteIndex === 5 && 
            (bitPosition === 1 || bitPosition === 2) &&
            (row.bit === 1 || row.bit === 2)) {
          
          const updatedMeaning = DecoderHelpers.updateBitMeaning(
            byteIndex,
            row.bit,
            row.value,
            localMatrixData,
            builderData,
            templateConfig,
            newHexValue
          )
          
          return {
            ...row,
            meaning: updatedMeaning || row.meaning
          }
        }
        
        return row
      })
    }
  }
  
  return updatedMatrix
}

function* handleTlvParseRequest(action) {
  try {
    const { data, isCustomTlv } = action.payload
    
    const cloudInput = {
      data: data,
      isCustomTlv: isCustomTlv,
    }
    
    const response = yield call(getResponse, cloudInput, "cloud/v1/TLV/TLV")
    
    if (response.resultCode !== "-1" && response.resultCode !== undefined) {
      yield put(tlvParseSuccess(response))
      
      // localStorage history management
      const history = JSON.parse(localStorage.getItem("jsonHistory")) || []
      history.push(response)
      localStorage.setItem("jsonHistory", JSON.stringify(history))
    } else {
      yield put(tlvParseFailure(response.resultDesc || "TLV parse operation failed"))
      
      // Still save to history even if failed
      const history = JSON.parse(localStorage.getItem("jsonHistory")) || []
      history.push(response)
      localStorage.setItem("jsonHistory", JSON.stringify(history))
    }
  } catch (error) {
    console.error('TLV Parse API Error:', error)
    yield put(tlvParseFailure(error.message || "Network error occurred"))
  }
}

function* decoderSaga() {
  yield takeEvery(DECODE_REQUEST, handleDecodeRequest)
  yield takeEvery(INITIALIZE_WITH_DEFAULT, handleDecodeRequest)
  yield takeEvery(TOGGLE_BIT, handleBitToggle)
  yield takeEvery(TLV_PARSE_REQUEST, handleTlvParseRequest)
}

export default decoderSaga
